import React, { useCallback, useState } from 'react';
import { trackEvent } from '../../analytics';
import TimelapseCalculator from '../TimelapseCalculator';
import { planToState, summarizePlan } from '../../lib/capturePlan';
import { saveCapturePlanToProject } from '../../lib/saveCapturePlan';
import './CapturePlan.css';

// Modal wrapper around the shared calculator core with a "Save to project"
// action. Holds its own draft state so editing never mutates the project
// until the user commits.
const CapturePlanDialog = ({ projectId, plan, onClose }) => {
  const [state, setState] = useState(() => planToState(plan));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const patch = useCallback((updates) => {
    setState((s) => ({ ...s, ...updates }));
  }, []);
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = await saveCapturePlanToProject(projectId, state);
      const summary = summarizePlan(payload);
      trackEvent('capture_plan_saved', {
        mode: state.mode,
        scenario: state.scenario,
        solve_for: state.solveFor,
        duration_days: summary ? summary.durationDays : null,
        frame_count: summary ? summary.frameCount : null,
      });
      onClose();
    } catch (e) {
      console.error('CapturePlanCard: save failed', e);
      setError('Could not save your plan. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="cp-overlay" role="dialog" aria-modal="true" aria-label="Capture plan">
      <div className="cp-modal">
        <div className="cp-modal-head">
          <div>
            <h3 className="cp-modal-title">Plan your capture</h3>
            <p className="cp-modal-sub">
              Dial in the interval and see the final video length and storage. Saving
              keeps it with the project — it doesn't change any footage.
            </p>
          </div>
          <button type="button" className="cp-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="cp-modal-body">
          {/* The core patches both `solveFor` and the legacy `mode` through
              onChange, so the dialog needs no separate solve-for handler. */}
          <TimelapseCalculator value={state} onChange={patch} compact />
        </div>

        {error && <div className="cp-modal-alert" role="alert">{error}</div>}

        <div className="cp-modal-actions">
          <button
            type="button"
            className="cp-btn cp-btn--ghost"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="cp-btn cp-btn--primary"
            onClick={handleSave}
            disabled={saving}
            data-testid="cp-save"
          >
            {saving ? 'Saving…' : 'Save to project'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Overview card: a saved-plan summary (recomputed from stored inputs) or an
// empty prompt, with a dialog to create/adjust the plan. Persistence is a
// direct owner-gated write of `capturePlan` on the project doc.
const CapturePlanCard = ({ projectId, capturePlan }) => {
  const [open, setOpen] = useState(false);
  const summary = summarizePlan(capturePlan);

  const openDialog = () => {
    trackEvent('capture_plan_opened', { has_plan: !!summary });
    setOpen(true);
  };

  return (
    <section className="cp-card" data-testid="capture-plan-card">
      <div className="cp-card-head">
        <div className="cp-card-heading">
          <h3 className="cp-card-title">Capture plan</h3>
          <p className="cp-card-sub">
            {summary
              ? 'Your saved interval, length and storage estimate for this project.'
              : 'Work out the right capture interval before you start shooting.'}
          </p>
        </div>
        <button
          type="button"
          className="cp-btn cp-btn--primary"
          onClick={openDialog}
          data-testid="cp-open"
        >
          {summary ? 'Adjust plan' : 'Create plan'}
        </button>
      </div>

      {summary ? (
        <div className="cp-summary" data-testid="cp-summary">
          <div className="cp-summary-item">
            <span className="cp-summary-label">Capture interval</span>
            <span className="cp-summary-value">{summary.intervalLabel}</span>
          </div>
          <div className="cp-summary-item">
            <span className="cp-summary-label">Over</span>
            <span className="cp-summary-value" data-testid="cp-summary-duration">
              {summary.weekdayLabel
                ? `${summary.durationLabel}, ${summary.weekdayLabel}`
                : summary.durationLabel}
            </span>
          </div>
          <div className="cp-summary-item">
            <span className="cp-summary-label">Final video</span>
            <span className="cp-summary-value">≈ {summary.finalVideoLabel}</span>
            {/* The headline is whichever finish the calculator dialog showed
                for this plan's solve direction; the other one is named
                underneath. summarizePlan drops it at 0% still footage, where
                the two are the same length and it would just repeat. */}
            {summary.altVideoLabel && (
              <span className="cp-summary-sub" data-testid="cp-summary-alt">
                {summary.altVideoName} ≈ {summary.altVideoLabel}
              </span>
            )}
          </div>
          <div className="cp-summary-item">
            <span className="cp-summary-label">Frames · storage</span>
            <span className="cp-summary-value">
              {summary.frameCount.toLocaleString()} · {summary.storageGB.toFixed(1)} GB
            </span>
          </div>
        </div>
      ) : (
        <p className="cp-empty" data-testid="cp-empty">
          No plan saved yet. Create one to estimate interval, final length and storage —
          it's stored with the project for reference and is fully optional.
        </p>
      )}

      {open && (
        <CapturePlanDialog
          projectId={projectId}
          plan={capturePlan}
          onClose={() => setOpen(false)}
        />
      )}
    </section>
  );
};

export default CapturePlanCard;
